import React from 'react';

function CommercialReportDisplay({ priorArtResults }) {
  if (!priorArtResults || priorArtResults.report_type !== 'commercial') {
    return null;
  }

  const execSummary = priorArtResults.commercial_executive_summary || {};
  const techHighlights = priorArtResults.commercial_technical_highlights || {};
  const competitiveIP = priorArtResults.commercial_competitive_ip || {};
  const partnerships = priorArtResults.commercial_partnerships || {};
  const citationInsights = priorArtResults.citation_insights || {};
  const results = priorArtResults.results || [];

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Commercial Report Header */}
      <div style={{ background: '#2e7d32', padding: '24px', borderRadius: '8px', color: 'white', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Commercial IP & Market Assessment</h2>
        <p style={{ fontSize: '14px', opacity: 0.8, marginTop: '8px' }}>Business development and investor-ready analysis</p>
      </div>

      {/* Value Proposition Box */}
      {execSummary.value_proposition && (
        <div style={{ background: '#e8f5e9', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '2px solid #4caf50' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '12px' }}>Value Proposition</h3>
          <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: '1.6' }}>{execSummary.value_proposition}</p>
        </div>
      )}

      {/* Quick Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>IP Strength</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: competitiveIP.ip_strength_rating === 'Strong' ? '#2e7d32' : competitiveIP.ip_strength_rating === 'Moderate' ? '#ff9800' : '#f44336' }}>
            {competitiveIP.ip_strength_rating || competitiveIP.ip_strength || 'Unknown'}
          </div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Market Opportunity</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>
            {execSummary.market_opportunity?.market_size || execSummary.market_opportunity?.target_market || 'TBD'}
          </div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Development Stage</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
            {execSummary.stage_of_development || 'Early'}
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      {(execSummary.executive_summary || execSummary.technology_summary) && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Executive Summary</h3>
          <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: '1.6' }}>{execSummary.executive_summary || execSummary.technology_summary}</p>
        </div>
      )}

      {/* IP & Prior Art Status */}
      {execSummary.ip_status_summary && (
        <div style={{ background: '#fff8e1', padding: '16px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ffcc02' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#f57c00', marginBottom: '8px' }}>📋 IP & Prior Art Status</h3>
          <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: '1.6' }}>{execSummary.ip_status_summary}</p>
        </div>
      )}

      {/* Investment Highlights */}
      {execSummary.investment_highlights && execSummary.investment_highlights.length > 0 && (
        <div style={{ background: '#fff8e1', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ffc107' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#f57c00', marginBottom: '12px' }}>Investment Highlights</h3>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {execSummary.investment_highlights.map((highlight, idx) => (
              <li key={idx} style={{ fontSize: '14px', color: '#333', marginBottom: '8px', lineHeight: '1.5' }}>{highlight}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Market Opportunity */}
      {execSummary.market_opportunity && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Market Opportunity</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Target Market</div>
              <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.target_market || 'Not specified'}</p>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Problem Solved</div>
              <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.problem_solved || 'Not specified'}</p>
            </div>
          </div>
          {execSummary.market_opportunity.competitive_advantage && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Competitive Advantage</div>
              <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.competitive_advantage}</p>
            </div>
          )}
          {(execSummary.market_opportunity.growth_rate || execSummary.market_opportunity.funding_landscape) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              {execSummary.market_opportunity.growth_rate && (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Growth Rate</div>
                  <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.growth_rate}</p>
                </div>
              )}
              {execSummary.market_opportunity.funding_landscape && (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Funding Landscape</div>
                  <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.funding_landscape}</p>
                </div>
              )}
            </div>
          )}
          {execSummary.market_opportunity.commercialization_route && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Commercialization Route</div>
              <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{execSummary.market_opportunity.commercialization_route}</p>
            </div>
          )}
        </div>
      )}

      {/* Citation Insights */}
      {citationInsights && citationInsights.summary && (
        <div style={{ background: '#f3e5f5', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ce93d8' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#7b1fa2', marginBottom: '12px' }}>IP Citation Strength</h3>
          <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px', lineHeight: '1.5' }}>{citationInsights.summary}</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Total Citations</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7b1fa2' }}>{citationInsights.total_forward_citations || 0}</div>
            </div>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Avg/Patent</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#5c6bc0' }}>{citationInsights.average_forward_citations || 0}</div>
            </div>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Citation Density</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#388e3c' }}>{citationInsights.citation_density || 'N/A'}</div>
            </div>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>Tech Maturity</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#f57c00' }}>{citationInsights.technology_maturity || 'N/A'}</div>
            </div>
          </div>

          {citationInsights.insights && citationInsights.insights.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {citationInsights.insights.slice(0, 3).map((insight, idx) => (
                <li key={idx} style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>{insight}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Technical Highlights */}
      {techHighlights.core_innovations && techHighlights.core_innovations.length > 0 && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Key Technical Innovations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {techHighlights.core_innovations.map((innovation, idx) => (
              <div key={idx} style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>{innovation.innovation || innovation}</div>
                {innovation.business_benefit && (
                  <div style={{ fontSize: '13px', color: '#666' }}>{innovation.business_benefit}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitive IP Landscape */}
      {competitiveIP.competitive_position_summary && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Competitive IP Landscape</h3>
          <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px', lineHeight: '1.6' }}>{competitiveIP.competitive_position_summary}</p>

          {competitiveIP.key_competitors && competitiveIP.key_competitors.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Key Competitors</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {competitiveIP.key_competitors.map((competitor, idx) => (
                  <span key={idx} style={{ background: '#e3f2fd', padding: '4px 12px', borderRadius: '16px', fontSize: '13px', color: '#1565c0' }}>
                    {typeof competitor === 'string' ? competitor : (competitor.competitor_type || competitor.competitor_name || competitor.name || 'Competitor')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(competitiveIP.white_space || competitiveIP.ip_strength_rationale) && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#e8f5e9', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '4px' }}>White Space Opportunity</div>
              <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>{competitiveIP.white_space || competitiveIP.ip_strength_rationale}</p>
            </div>
          )}

          {competitiveIP.barriers_to_entry && competitiveIP.barriers_to_entry.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Barriers to Entry</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {competitiveIP.barriers_to_entry.map((barrier, idx) => (
                  <li key={idx} style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>{barrier}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Partnership Opportunities */}
      {partnerships.partnership_summary && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Partnership & Go-to-Market</h3>
          <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px', lineHeight: '1.6' }}>{partnerships.partnership_summary}</p>

          {partnerships.ideal_partner_profile && (
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Ideal Partner Profile</div>
              <div style={{ fontSize: '13px', color: '#555' }}>
                <div><strong>Company Size:</strong> {partnerships.ideal_partner_profile.company_size || 'Any'}</div>
                <div><strong>Key Capabilities:</strong> {partnerships.ideal_partner_profile.key_capabilities_needed || 'Not specified'}</div>
                <div><strong>Geographic Focus:</strong> {partnerships.ideal_partner_profile.geographic_focus || 'Global'}</div>
                {partnerships.ideal_partner_profile.strategic_fit && (
                  <div><strong>Strategic Fit:</strong> {partnerships.ideal_partner_profile.strategic_fit}</div>
                )}
              </div>
            </div>
          )}

          {(partnerships.target_companies || partnerships.target_partners) && (partnerships.target_companies || partnerships.target_partners).length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Potential Target Companies</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(partnerships.target_companies || partnerships.target_partners).map((company, idx) => (
                  <span key={idx} style={{ background: '#fff3e0', padding: '4px 12px', borderRadius: '16px', fontSize: '13px', color: '#e65100' }}>
                    {typeof company === 'string' ? company : company.name || company}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prior Art References */}
      {results && results.length > 0 && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #ddd' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '12px' }}>Prior Art References ({results.length} analyzed)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {results.slice(0, 10).map((patent, idx) => (
              <div key={idx} style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <a 
                    href={`https://patents.google.com/patent/US${patent.patent_number}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ fontWeight: 'bold', color: '#1565c0', textDecoration: 'none' }}
                  >
                    US{patent.patent_number}
                  </a>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: 'bold',
                    color: patent.similarity_score >= 70 ? '#c62828' : patent.similarity_score >= 50 ? '#f57c00' : '#666'
                  }}>
                    {patent.similarity_score}%
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>{patent.title?.substring(0, 60)}...</div>
                <div style={{ fontSize: '11px', color: '#666' }}>{patent.assignee?.substring(0, 40)}</div>
                <div style={{ fontSize: '11px', color: '#7b1fa2', marginTop: '4px' }}>Cited by {patent.forward_citations || 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Next Steps */}
      {execSummary.recommended_next_steps && execSummary.recommended_next_steps.length > 0 && (
        <div style={{ background: '#1565c0', padding: '20px', borderRadius: '8px', color: 'white', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>Recommended Next Steps</h3>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            {execSummary.recommended_next_steps.map((step, idx) => (
              <li key={idx} style={{ marginBottom: '8px', fontSize: '14px' }}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Academic Papers Section */}
      {priorArtResults.papers && priorArtResults.papers.length > 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e40af', marginBottom: '16px' }}>
            Academic Papers ({priorArtResults.papers_count || priorArtResults.papers.length})
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
            Non-patent literature indicating research activity. High citations suggest significant interest in this technology area.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {priorArtResults.papers.slice(0, 5).map((paper, idx) => (
              <div key={idx} style={{ background: 'white', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '6px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  {paper.title?.substring(0, 80)}{paper.title?.length > 80 ? '...' : ''}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {paper.venue || 'Unknown venue'} • {paper.publication_year || 'N/A'} • {paper.cited_by_count || 0} citations
                </div>
                <div style={{ marginTop: '6px' }}>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: paper.publication_status === 'BEFORE_FILING' ? '#dcfce7' : 
                               paper.publication_status === 'AFTER_FILING' ? '#fee2e2' : '#fef3c7',
                    color: paper.publication_status === 'BEFORE_FILING' ? '#16a34a' : 
                           paper.publication_status === 'AFTER_FILING' ? '#dc2626' : '#d97706'
                  }}>
                    {paper.publication_status === 'BEFORE_FILING' ? 'Before Filing (Prior Art)' :
                     paper.publication_status === 'AFTER_FILING' ? 'After Filing' : 'Date Uncertain'}
                  </span>
                  <div style={{ display: 'flex', gap: '10px', marginLeft: '10px' }}>
                    {paper.doi && <a href={'https://doi.org/' + paper.doi} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#0369a1', textDecoration: 'none' }}>View Paper ↗</a>}
                    {paper.pdf_url && <a href={paper.pdf_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#0369a1', textDecoration: 'none' }}>PDF ↗</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px', fontSize: '12px', color: '#666' }}>
        <strong>Disclaimer:</strong> This commercial assessment is for business development and investor discussion purposes only.
        It is not legal advice, a formal patentability opinion, or freedom-to-operate analysis.
        Consult qualified patent counsel before making IP-related business decisions.
      </div>
    </div>
  );
}

export default CommercialReportDisplay;